/**
 * Gives the PDP shelf configurator's four dimension <select> elements an accessible name.
 *
 * The us-configurator app's theme app extension
 * (cdn.shopify.com/extensions/.../us-configurator-7/assets/configurator.js, app block
 * shopify://apps/us-configurator/blocks/configurator/...) renders the whole configurator
 * client-side inside `div[data-shelf-configurator]` — the server HTML ships only an empty
 * skeleton — on product.outdoor-floating-shelf / artisan-shelves / mantel-product /
 * ppm-custom-shelf-product. A live WebKit axe capture (2026-09-28) flagged all four selects for
 * select-name (impact critical):
 *   select[data-field="length_whole"], select[data-field="length_frac"],
 *   select[data-field="depth_whole"], select[data-field="depth_frac"]
 * None has an id/name/aria-label/aria-labelledby. Each does have a visible sibling
 * `<span class="shelf-configurator__label">` inside its `.shelf-configurator__field` wrapper:
 * "Length (in)" / "Depth (in)" for the whole-number selects, and a bare, ambiguous "Fraction"
 * for both fraction selects (the Thickness radiogroup a few rows up already carries
 * aria-label="Thickness" — the app has the pattern, it just didn't apply it here).
 *
 * Fix, in priority order:
 *   1. Point aria-labelledby at that visible label span, so the accessible name matches what's
 *      on screen (WCAG 2.5.3 label-in-name) — "Length (in)" / "Depth (in)".
 *   2. For the two "Fraction" selects, fold their dimension's whole-field label in too
 *      (aria-labelledby="<wholeLabelId> <fracLabelId>"), so the computed name disambiguates:
 *      "Length (in) Fraction" / "Depth (in) Fraction" instead of both selects sharing the bare
 *      word "Fraction".
 *   3. Only if no label span exists at all (defensive — the app's markup could change), fall
 *      back to aria-label built from a localized string (window.theme.strings.shelfConfigurator,
 *      wired in layout/theme.liquid; a hardcoded English default covers the case where that
 *      object failed to load, since this is a legal-priority a11y fix and must not go silent).
 *
 * Like assets/judgeme-a11y-patches.js: an initial sweep for a configurator already on the page, plus
 * a MutationObserver for one inserted later (the container can appear after this script runs).
 * Once a container is found it gets its own scoped observer on its subtree, in case the app
 * rebuilds `.shelf-configurator__body` later (recon showed no such rebuild in practice, only
 * price/error-visibility mutations — this is belt-and-suspenders, not a response to a known
 * re-render). Idempotent via a data attribute on each select, so the observer never reprocesses a
 * select it already fixed. Never overwrites an existing author-provided accessible name
 * (aria-label / aria-labelledby / an associated <label for>) — if the app ever ships its own,
 * this leaves it alone, same rule judgeme-a11y-patches.js follows for Judge.me's own markup. Every DOM
 * read is wrapped defensively — this must never throw or break the page.
 */
(() => {
  const PROCESSED_ATTR = 'data-shelf-a11y-fixed';
  const OBSERVED_ATTR = 'data-shelf-a11y-observed';
  const CONTAINER_SELECTOR = '[data-shelf-configurator]';
  const SELECT_SELECTOR = 'select[data-field]';
  const LABEL_SELECTOR = '.shelf-configurator__label';
  const FIELD_WRAPPER_SELECTOR = '.shelf-configurator__field';
  const ID_PREFIX = 'shelf-configurator-label-a11y';

  const FALLBACK_STRINGS = {
    length: 'Length (in)',
    depth: 'Depth (in)',
    fraction: 'Fraction'
  };

  let idCounter = 0;

  /**
   * Mints an id that isn't already used anywhere in the document.
   * @param {string} prefix - Id prefix.
   * @returns {string} A fresh, unused id.
   */
  function uniqueId(prefix) {
    let id;
    do {
      idCounter += 1;
      id = `${prefix}-${idCounter}`;
    } while (document.getElementById(id));
    return id;
  }

  /**
   * Reads a shelf-configurator string from the localized strings theme.liquid wires up, falling
   * back to the (English) default so this shim keeps working even if that wiring is missing.
   * @param {string} key - 'length' | 'depth' | 'fraction'.
   * @returns {string} The string to use.
   */
  function getString(key) {
    try {
      const strings = window.theme && window.theme.strings && window.theme.strings.shelfConfigurator;
      if (strings && strings[key]) return strings[key];
    } catch (error) {
      // Defensive.
    }
    return FALLBACK_STRINGS[key];
  }

  /**
   * True if a select already has an accessible name from something other than this shim — an
   * author-provided aria-label, aria-labelledby, or an associated <label for>.
   * @param {Element} select - Candidate <select>.
   * @returns {boolean}
   */
  function hasAuthoredName(select) {
    try {
      const label = select.getAttribute('aria-label');
      if (label && label.trim()) return true;

      const labelledby = select.getAttribute('aria-labelledby');
      if (labelledby && labelledby.trim()) return true;

      if (select.id && document.querySelector(`label[for="${select.id}"]`)) return true;
    } catch (error) {
      // Defensive.
    }
    return false;
  }

  /**
   * @param {Element} select - A configurator <select>.
   * @returns {Element} The select's `.shelf-configurator__field` wrapper, or its parent if the
   *   app's wrapper class ever changes.
   */
  function findFieldWrapper(select) {
    try {
      const wrapper = select.closest(FIELD_WRAPPER_SELECTOR);
      if (wrapper) return wrapper;
    } catch (error) {
      // Defensive.
    }
    return select.parentElement;
  }

  /**
   * @param {Element|null} wrapper - A field wrapper.
   * @returns {Element|null} Its visible `.shelf-configurator__label` span, if any.
   */
  function findLabelSpan(wrapper) {
    if (!wrapper || !wrapper.querySelector) return null;
    try {
      return wrapper.querySelector(LABEL_SELECTOR);
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {Element} el - Element that needs an id to be referenced by aria-labelledby.
   * @returns {string} Its existing id, or a freshly minted one.
   */
  function ensureId(el) {
    if (!el.id) el.id = uniqueId(ID_PREFIX);
    return el.id;
  }

  /**
   * @param {Element} container - The `[data-shelf-configurator]` root.
   * @param {string} dimension - 'length' | 'depth'.
   * @returns {Element|null} That dimension's whole-number <select>, if present.
   */
  function findWholeSelect(container, dimension) {
    try {
      return container.querySelector(`select[data-field="${dimension}_whole"]`);
    } catch (error) {
      return null;
    }
  }

  /**
   * Applies an accessible name to a single dimension <select>, unless it already has one.
   * @param {Element} select - A configurator <select data-field="...">.
   * @param {Element} container - Its enclosing `[data-shelf-configurator]` root.
   */
  function fixSelect(select, container) {
    if (!select || !select.hasAttribute || select.hasAttribute(PROCESSED_ATTR)) return;

    try {
      if (hasAuthoredName(select)) {
        select.setAttribute(PROCESSED_ATTR, 'true');
        return;
      }

      const field = select.getAttribute('data-field') || '';
      const [dimension, suffix] = field.split('_');
      const wrapper = findFieldWrapper(select);
      const ownLabel = findLabelSpan(wrapper);

      if (suffix === 'frac' && dimension) {
        const wholeSelect = findWholeSelect(container, dimension);
        const wholeLabel = wholeSelect ? findLabelSpan(findFieldWrapper(wholeSelect)) : null;

        if (wholeLabel && ownLabel) {
          select.setAttribute('aria-labelledby', `${ensureId(wholeLabel)} ${ensureId(ownLabel)}`);
        } else {
          select.setAttribute('aria-label', `${getString(dimension)} ${getString('fraction')}`);
        }
      } else if (ownLabel) {
        select.setAttribute('aria-labelledby', ensureId(ownLabel));
      } else {
        select.setAttribute('aria-label', getString(dimension === 'depth' ? 'depth' : 'length'));
      }

      select.setAttribute(PROCESSED_ATTR, 'true');
    } catch (error) {
      // Defensive — never let this shim break the page.
    }
  }

  /**
   * Fixes every dimension <select> currently inside one configurator container.
   * @param {Element} container - A `[data-shelf-configurator]` root.
   */
  function sweepContainer(container) {
    if (!container || !container.querySelectorAll) return;
    try {
      container.querySelectorAll(SELECT_SELECTOR).forEach((select) => fixSelect(select, container));
    } catch (error) {
      // Defensive.
    }
  }

  /**
   * Starts watching one configurator container: an immediate sweep, plus (if not already
   * watching this exact container) a MutationObserver scoped to its own subtree so any field
   * inserted or rebuilt after the initial render still gets fixed.
   * @param {Element} container - A `[data-shelf-configurator]` root.
   */
  function watchContainer(container) {
    if (!container || !container.hasAttribute || container.hasAttribute(OBSERVED_ATTR)) return;
    container.setAttribute(OBSERVED_ATTR, 'true');

    sweepContainer(container);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => sweepContainer(container));
      observer.observe(container, { childList: true, subtree: true });
    }
  }

  /**
   * @param {ParentNode} root - Node to search within (document, or a newly-inserted subtree).
   * @returns {Element[]} Every `[data-shelf-configurator]` container found at or under root.
   */
  function findContainers(root) {
    const found = [];
    try {
      // nodeType 1 = element, 9 = document. Avoids relying on the global `Node` constant, which
      // isn't declared for every asset script in this theme's lint config.
      if (root.nodeType !== undefined && root.nodeType !== 1 && root.nodeType !== 9) return found;

      if (root.matches) {
        try {
          if (root.matches(CONTAINER_SELECTOR)) found.push(root);
        } catch (error) {
          // Defensive.
        }
      }

      if (root.querySelectorAll) {
        root.querySelectorAll(CONTAINER_SELECTOR).forEach((el) => found.push(el));
      }
    } catch (error) {
      // Defensive.
    }
    return found;
  }

  // Initial sweep — the configurator may already be on the page (a fast client render that beat
  // this script) or not yet (the common case: server HTML is an empty skeleton).
  findContainers(document).forEach(watchContainer);

  // The app block inserts its container after this script runs. Watch the whole document for it,
  // same pattern as judgeme-a11y-patches.js; each container found gets handed off to its own scoped
  // observer above (watchContainer), which is where late/rebuilt fields actually get fixed.
  if ('MutationObserver' in window) {
    const documentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // 1 = element
          findContainers(node).forEach(watchContainer);
        });
      });
    });

    documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
